import { getCompressedImageUrl } from 'client/helpers/getCompressedImageUrl';
import * as React from 'react';
import Slider, { Settings } from 'react-slick';
import LazyImage from './LazyImage';

interface IProps {
  media?: Array<string | { url?: string }>;
  autoPlay?: boolean;
}

interface SliderTypes {
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const SampleNextArrow: React.FC<SliderTypes> = ({ className, style, onClick }) => (
  <div
    className={className}
    style={{ ...style, display: 'block', background: 'none', right: '0px' }}
    onClick={onClick}
  />
);

const SamplePrevArrow: React.FC<SliderTypes> = ({ className, style, onClick }) => (
  <div
    className={className}
    style={{ ...style, display: 'block', background: 'none', left: '0px' }}
    onClick={onClick}
  />
);

const settings: Settings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  nextArrow: <SampleNextArrow />,
  prevArrow: <SamplePrevArrow />,
  adaptiveHeight: true,
  lazyLoad: 'ondemand',
};

const INVALID_IMAGES = [
  'https://docs.pgiconsult.fr/prod/kepler/produits_photos/1033/13434.jpg',
  'https://docs.pgiconsult.fr/prod/kepler/produits_photos/1503/19198.jpg',
  'https://docs.pgiconsult.fr/prod/kepler/produits_photos/702/43563.jpg',
];

const normalizeMedia = (media?: Array<string | { url?: string }>) => {
  if (!Array.isArray(media)) return [];

  return media
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') return item.url || '';
      return '';
    })
    .filter(Boolean)
    .filter((url) => !INVALID_IMAGES.includes(url as string))
    .map((url) => `${getCompressedImageUrl(url as string)}?tr=h-357`);
};

const ImageCarousel: React.FC<IProps> = ({ media, autoPlay }) => {
  const [failedImages, setFailedImages] = React.useState<string[]>([]);

  const mediaArray = React.useMemo(() => normalizeMedia(media), [media]);

  const validImages = React.useMemo(
    () => mediaArray.filter((url) => !failedImages.includes(url)),
    [mediaArray, failedImages],
  );

  const handleImageError = React.useCallback((src: string) => {
    setFailedImages((prev) => (prev.includes(src) ? prev : [...prev, src]));
  }, []);

  if (!validImages.length) {
    return (
      <img
        src="/assets/img/placeholder.jpg"
        alt="produit"
        style={{
          width: '100%',
          height: '357px',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    );
  }

  if (validImages.length === 1) {
    return (
      <LazyImage
        src={validImages[0]}
        height="357px"
        width="100%"
        alt="produit"
        className="object-fit"
        style={{
          marginBottom: '-8px',
          objectFit: 'cover',
          display: 'block',
        }}
        onError={() => handleImageError(validImages[0])}
      />
    );
  }

  return (
    <Slider {...settings} autoplay={autoPlay} autoplaySpeed={5000}>
      {validImages.map((url) => (
        <LazyImage
          key={url}
          src={url}
          height="357px"
          width="100%"
          alt="produit"
          className="object-fit"
          style={{
            marginBottom: '-8px',
            objectFit: 'cover',
            display: 'block',
          }}
          onError={() => handleImageError(url)}
        />
      ))}
    </Slider>
  );
};

export default ImageCarousel;
